import type { PersonId } from '@shared/brand';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import { expiringLive, newestLive, totalAvailable, type LiveBalance } from '../../balancePeriod';

/**
 * Cómo se ordena y cómo se marca la tabla «Detalle por persona».
 *
 * Vive aparte de la pantalla porque son dos decisiones con bordes —quién sale
 * primero cuando dos personas tienen los mismos días, y de dónde sale la fecha de
 * vencimiento— y porque la pantalla no debería tener que saber ninguno de los dos.
 */

/** Lo que necesita el orden, y nada más. */
type Row = Pick<PersonBalanceRow, 'personName' | 'remainingDays'>;

/**
 * Una fila de «Detalle por persona»: todos sus periodos vigentes en una cifra, y
 * el que está por vencer aparte.
 *
 * Con «Vencimiento del saldo» en más de 12 meses una persona tiene vigentes el
 * periodo nuevo y lo que quede del anterior. La tabla suma los dos —es lo que
 * puede pedir hoy— y marca en rojo sólo los días que se pierden pronto, con su
 * fecha y su cifra: ya no es el saldo entero lo que vence, sino lo del periodo
 * viejo.
 *
 * **Se deriva del aniversario, no se lee de `expiringDays` ni de `expiresOn`.**
 * Esos dos campos del documento sólo se refrescan cuando corre `ensureBalance`, y
 * quien lleva meses sin pedir nada —justo a quien hay que ver— los trae viejos o
 * en cero. Ver `expiringLive`: la misma cuenta que la barra lateral y la misma
 * ventana que el recordatorio semanal `remindExpiringDays`.
 */
export interface PersonBalanceRow {
  readonly id: string;
  readonly personId: PersonId;
  readonly personName: string;
  /** Del periodo nuevo: lo que le toca y lo que ya tomó de él. */
  readonly entitledDays: number;
  readonly takenDays: number;
  /** Entre todos los periodos vigentes. */
  readonly remainingDays: number;
  /** Lo que se pierde pronto, o `null` si nada vence dentro de la ventana. */
  readonly expiring: { readonly days: number; readonly lastValidDay: CalendarDate } | null;
}

export const toPersonBalanceRow = (
  lives: readonly LiveBalance[],
  hireDate: CalendarDate | null,
  today: CalendarDate,
): PersonBalanceRow | null => {
  const current = newestLive(lives);
  if (current === null) return null;
  const expiring = expiringLive(lives, hireDate, today);
  return {
    id: current.balance.id,
    personId: current.balance.personId,
    personName: current.balance.personName,
    entitledDays: current.balance.entitledDays,
    takenDays: current.balance.takenDays,
    remainingDays: totalAvailable(lives),
    expiring:
      expiring === null ? null : { days: expiring.available, lastValidDay: expiring.lastValidDay },
  };
};

/**
 * De más días disponibles a menos.
 *
 * El desempate es el nombre y no el orden en que vinieron: la consulta los trae
 * por lo que le convenga al índice, y una tabla que se reordena sola entre dos
 * cargas hace dudar de si cambió el dato o sólo la fila.
 */
export const byRemainingDaysDesc = (a: Row, b: Row): number =>
  b.remainingDays - a.remainingDays || a.personName.localeCompare(b.personName, 'es');
